import * as React from 'react';

import { DropdownInput } from './dropdownInput';

import useModalAccessibility from '../../hooks/useModalAccessibility';

const closeIconSrc = `${window.postbuildEnv.CDN_URL}/images/icons/close.svg`;

// Sidebar orientation constants
export const VERTICAL = 'vertical' as const;
export const HORIZONTAL = 'horizontal' as const;
export type SidebarOrientation = typeof VERTICAL | typeof HORIZONTAL;

interface ProgressReportsSidebarProps {
  additionalDropdowns?: React.ReactNode;
  children?: React.ReactNode;
  classroomOptions?: Array<{ id: number; label: string; value: number }>;
  closeMobileSidebar?: () => void;
  isMobileOnly?: boolean;
  onClassroomChange?: (classroom: any) => void;
  orientation?: SidebarOrientation;
  renderAsWrapper?: boolean;
  selectedClassroom?: any;
  showMobileSidebar?: boolean;
  showSidebarButton?: React.ReactNode;
  topSection?: React.ReactNode;
  ariaLabel?: string;
  filterButtons?: React.ReactNode;
}

const ProgressReportsSidebar: React.FC<ProgressReportsSidebarProps> = ({
  additionalDropdowns,
  children,
  classroomOptions = [],
  closeMobileSidebar = () => {},
  isMobileOnly = false,
  onClassroomChange,
  orientation = VERTICAL,
  renderAsWrapper = true,
  selectedClassroom,
  showMobileSidebar = false,
  showSidebarButton,
  topSection,
  ariaLabel,
  filterButtons,
}) => {
  const { modalRef } = useModalAccessibility(closeMobileSidebar, showMobileSidebar);

  const mobileAttributes = showMobileSidebar ? {
    'aria-label': ariaLabel || 'Progress Reports Navigation',
    'aria-modal': true,
    ref: modalRef,
    role: 'dialog',
    tabIndex: -1
  } : {};

  const mobileOnlyClass = isMobileOnly ? 'hide-on-mobile' : '';
  const mobileVisibilityClass = showMobileSidebar ? 'mobile-open' : 'mobile-hidden';
  const orientationClass = `sidebar-container-${orientation}`;
  const sidebarContainerClass = `sidebar-container ${orientationClass} ${mobileOnlyClass} ${mobileVisibilityClass}`.trim();

  const sidebarContent = (
    <React.Fragment>
      {!isMobileOnly && showMobileSidebar && (
        <div className="top-section">
          {topSection || (
            <button aria-expanded={true} className="interactive-wrapper focus-on-light" onClick={closeMobileSidebar} type="button">
              <img alt="" src={closeIconSrc} />
              Close
            </button>
          )}
        </div>
      )}
      <div className="sidebar">
        {showSidebarButton && (
          <div className="sidebar-section show-sidebar-button-wrapper">
            {showSidebarButton}
          </div>
        )}
        {onClassroomChange && (
          <div className="sidebar-section dropdowns-section">
            <DropdownInput
              className="bordered small"
              disabled={!classroomOptions.length}
              handleChange={onClassroomChange}
              id="classroom-dropdown"
              isSearchable={true}
              label="Class"
              options={classroomOptions}
              placeholder="No Classrooms"
              value={selectedClassroom}
            />
            {additionalDropdowns}
          </div>
        )}
        {children}
      </div>
    </React.Fragment>
  );

  if (!renderAsWrapper) {
    return sidebarContent;
  }

  const SidebarElement = showMobileSidebar ? 'div' : 'aside';

  return (
    <SidebarElement className={sidebarContainerClass} data-testid="sidebar" {...mobileAttributes}>
      {sidebarContent}
      {filterButtons}
    </SidebarElement>
  );
};

export { ProgressReportsSidebar };
export default ProgressReportsSidebar;
